import { NotFoundError } from 'src/shared/error';

import type { UtilityContextWithTracker } from 'src/app/shared/contexts/asyncData';

import type { PsfPromoteOrShareDataQuery, PsfPromoteOrShareDataQueryVariables } from './api.graphql';
import { PsfPromoteOrShareData } from './api.graphql';
import { handleRedirect } from './handleRedirect';
import { normalizePsfComboRemoveAutoProgressionExperiment } from './normalize/normalizePsfComboRemoveAutoProgressionExperiment';
import { normalizeData } from './normalizeData';
import { getRecruiter } from './recruiter';
import type { PsfPromoteOrShareRecruiterQuery } from './recruiter.graphql';
import type { PsfPromoteOrSharePageData } from './types';
import { PsfPromoteOrSharePageDataError } from './types';

export type { PsfPromoteOrSharePageData };

type FetchOptions = {
	recruiterId?: string;
	banditOverrides: { combo?: string };
};

type FetchResult = {
	petitionData: { data?: PsfPromoteOrShareDataQuery | null };
	recruiterData?: PsfPromoteOrShareRecruiterQuery | null | undefined;
};

type Params = {
	slug: string;
	locale: string;
	countryCode: string;
};

async function fetchPageDependencies(
	utilityContext: UtilityContextWithTracker,
	params: Params,
	options: FetchOptions,
): Promise<FetchResult> {
	const { gql } = utilityContext;
	const { recruiterId, banditOverrides } = options;

	const [petitionResult, recruiterResult] = await Promise.all([
		gql.fetch<PsfPromoteOrShareDataQuery, PsfPromoteOrShareDataQueryVariables>({
			query: PsfPromoteOrShareData,
			variables: {
				slugOrId: params.slug,
				includeBanditComboOverride: !!banditOverrides.combo,
				banditComboOverrideVariantName: banditOverrides.combo || '',
				locale: params.locale,
				countryCode: params.countryCode,
			},
			rejectOnError: true,
		}),
		recruiterId ? getRecruiter(utilityContext, recruiterId) : Promise.resolve({ data: undefined }),
	]);

	return {
		petitionData: petitionResult,
		recruiterData: recruiterResult.data?.recruiter ? { recruiter: recruiterResult.data.recruiter } : undefined,
	};
}

export async function getPsfPromoteOrSharePageData(
	utilityContext: UtilityContextWithTracker,
	params: Params,
	options: FetchOptions,
): Promise<PsfPromoteOrSharePageData> {
	const { petitionData, recruiterData } = await fetchPageDependencies(utilityContext, params, options);

	const { data } = petitionData;

	if (!data?.petition) {
		throw new NotFoundError();
	}

	if (!data.petition.promotionStats) {
		throw new PsfPromoteOrSharePageDataError('psf_promote_or_share_page_missing_promotion_stats');
	}

	handleRedirect(utilityContext.track, {
		petition: data.petition,
		viewer: data.viewer,
		fcmValues: data.featureConfigs,
	});

	const pageData = normalizeData(utilityContext, {
		petition: data.petition,
		promotionStats: data.petition.promotionStats,
		viewer: data.viewer,
		recruiter: recruiterData?.recruiter || undefined,
		featureConfigs: data.featureConfigs,
	});

	const psfComboRemoveAutoProgressionVariation = await normalizePsfComboRemoveAutoProgressionExperiment({
		experiment: data.viewer.experiments.psfComboRemoveAutoProgressionExperiment,
		utilityContext,
		isShareSlideUpEnabled: data.featureConfigs.isShareSlideUpEnabled,
	});

	return {
		...pageData,
		psfComboRemoveAutoProgressionExperiment: { variation: psfComboRemoveAutoProgressionVariation },
	};
}
